import { AuctionStates, ProductStates } from "../../../utils/misc";
import { CANONICAL_DOMAIN } from "../../../config";

const POSTALS_STANDARD = ["standard", "custom"];
const POSTAL_REGISTERED = "registered";
const CATEGORY_ART = "kunst";

/**
 * @typedef {import("../../../Apollo/schema").Product} Product
 * @typedef {import("../../../Apollo/schema").ProductBySlugQuery['productBySlug']} ProductBySlug
 * @typedef {import("../../../Apollo/schema").Merchant} Member
 */

const LABEL_MAP = {
  // ["ACCEPTED,RESERVED"]: {
  //   title: "common:auction-state-reserved",
  //   className: "bg-[#EADEE0] text-[#5A272E]",
  //   priority: true,
  // },
  sold: {
    title: "common:auction-state-already-sold",
    className: "bg-[#FFF7F7] text-[#E26F77]",
    priority: true,
  },
  ["ACCEPTED,COMING_SOON"]: {
    title: "common:common-coming-soon",
    className: "bg-[#282828] text-[#FFFFFF]",
  },
};

const ProductUtils = {
  /**
   * Get rich snippet (share) of the product.
   *
   * @param {Product} [product]
   * @param {string} [locale]
   * @param {string} [defaultLocale]
   */
  getRichSnippet: (
    product = undefined,
    locale = undefined,
    defaultLocale = undefined
  ) => {
    if (!product) {
      return null;
    }

    const displayPrice = product?.auction?.allow_bid
      ? product?.auction?.minimum_bid
      : product?.auction?.buy_now_price;

    const isSold = Boolean(product?.auction?.sold_at);

    const availability = isSold
      ? "https://schema.org/OutOfStock"
      : "https://schema.org/InStock";

    const brand = product?.attributes?.find(
      ({ __typename }) => __typename === "Brand"
    )?.title;

    const prefix = locale === defaultLocale ? "" : locale;

    const url = new URL(
      `${prefix}/products/${product?.slug}`,
      `https://${CANONICAL_DOMAIN}`
    );

    return {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product?.title,
      image: product?.images.map(x => x?.url).filter(Boolean),
      description: product?.description,
      offers: {
        "@type": "Offer",
        url,
        availability,
        priceCurrency: displayPrice?.currency,
        price: displayPrice?.amount?.toString(),
        itemCondition: "https://schema.org/UsedCondition",
      },
      brand: brand ? { "@type": "Brand", name: brand } : undefined,
    };
  },
  /**
   * Get product price info.
   *
   * @param {Product | ProductBySlug} product
   */
  getPriceInfo: product => {
    const buyNowPrice = product.auction?.buy_now_price;
    const buyNowPriceAmount = buyNowPrice?.amount ?? 0;

    const originalPrice = product.original_price;
    const originalPriceAmount = originalPrice?.amount ?? buyNowPriceAmount;

    const previousPrice =
      buyNowPriceAmount < originalPriceAmount ? originalPrice : undefined;

    const discountPercentage = Math.round(
      ((previousPrice?.amount - buyNowPrice?.amount) / previousPrice?.amount) *
        100
    );

    const isLoweredInPrice =
      (buyNowPrice?.amount ?? 0) < (previousPrice?.amount ?? 0);

    const soldPrice = product?.auction?.sold_at;

    const bidPrice =
      product?.auction?.allow_bid && product?.auction?.minimum_bid
        ? product?.auction?.minimum_bid
        : undefined;

    return {
      price: buyNowPrice,
      previousPrice,
      soldPrice,
      bidPrice,
      discountPercentage,
      isLoweredInPrice,
    };
  },
  /**
   * Get label based on product state / auction state.
   *
   * @param {Product} product
   */
  getLabel: product => {
    const sold = product?.auction?.sold_at ? LABEL_MAP.sold : undefined;

    const productState = product.state;
    const auctionState = product.auction?.state;

    const key = `${productState},${auctionState}`;
    return sold ?? LABEL_MAP[key];
  },

  /**
   * Is the product sold based on auction and product state.
   *
   */
  isSold: product => {
    return Boolean(product?.auction?.sold_at);
  },

  /**
   * Is the product reserved based on auction and product state.
   *
   * @param {Product} product
   */
  isReserved: product => {
    const isSold = Boolean(product?.auction?.sold_at);

    return !isSold && Boolean(product?.auction?.state === "RESERVED");
  },

  /**
   * Bid only product state.
   *
   * @param {Product | ProductBySlug} product
   */
  isBidOnly: product => {
    return (
      product?.auction?.state === AuctionStates.BID_ONLY &&
      product?.state === ProductStates.ACCEPTED
    );
  },

  /**
   * Allow product bid.
   *
   * @param {Product | ProductBySlug} product
   */
  allowBid: product => {
    return product.auction?.allow_bid ?? false;
  },

  /**
   * Is coming soon product state.
   *
   * @param {Product} product
   */
  isComingSoon: product => {
    return (
      product?.auction?.state === AuctionStates.COMING_SOON &&
      product?.state === ProductStates.ACCEPTED
    );
  },

  /**
   * Get info if user is an product owner.
   *
   * @param {Product | ProductBySlug} product
   * @param {Merchant} merchant
   */
  isProductOwner: (product, merchant) => {
    const productMerchantId = product.merchant?.id;
    const merchantId = merchant?.id;

    return productMerchantId === merchantId;
  },

  /**
   * Is the product of category ART.
   *
   * @param {Product} product
   *
   */
  isArt: product => {
    const categories = product.categories.filter(cat => cat.level === 0);
    const category = categories[0]?.slug;

    return category === CATEGORY_ART;
  },

  /**
   * Is the product/ad Expiring.
   *
   * @param {Product} product
   *
   */
  isExpiring: product => {
    return Boolean(product?.status === "EXPIRING");
  },

  isActive: product => {
    return Boolean(product?.status === "ACTIVE");
  },

  /**
   * Loved product label.
   *
   * @param {Product} product
   *
   */
  isLovedByWhoppah: product => {
    return product.attributes.reduce((acc, curr) => {
      if (
        curr.__typename.toLocaleUpperCase() === "LABEL" &&
        curr?.slug === "whoppah-loves"
      ) {
        acc = true;
      }
      return acc;
    }, null);
  },

  hasDelivery: product => {
    return product?.delivery_method === "DELIVERY";
  },

  hasDeliveryOrPickupDelivery: product => {
    return product?.delivery_method === "PICKUP_DELIVERY";
  },

  hasOnlyPickupDelivery: product => {
    return product?.delivery_method === "PICKUP";
  },

  hasCourierShipping: product => {
    return product?.shipping_method?.slug === "courier";
  },

  hasPostalShipping: product => {
    return product?.shipping_method?.slug === "postal";
  },

  hasStandardPostalShipping: product => {
    return POSTALS_STANDARD.includes(product?.shipping_method?.slug);
  },

  hasRegisteredPostalShipping: product => {
    return product?.shipping_method?.slug === POSTAL_REGISTERED;
  },

  isInShowroom: product => {
    return Boolean(product?.is_in_showroom);
  },

  hasGroundFloorDelivery: product => {
    return Boolean(product?.ground_floor_only_delivery);
  },

  hasMobileARModel: product => {
    const android_arobject =
      product?.android_arobject ?? product?.product_type?.android_arobject;
    const ios_arobject =
      product?.ios_arobject ?? product?.product_type?.ios_arobject;

    return android_arobject || ios_arobject;
  },
};

export default ProductUtils;
